import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { isAxiosError } from 'axios';
import {
  ChevronDown,
  ChevronUp,
  CircleAlert,
  MapPin,
  Save,
} from 'lucide-react';

import { Button } from 'components/Button';
import { ValueType } from 'components/DebounceSelect';
import { Dialog, DialogContent } from 'components/Dialog';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Input } from 'components/input/Input';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { FormatPhoneNumber } from 'components/input/RHFTextInput';
import { Divider, Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { useToast } from 'hooks/useToaster';
import { createLocation } from 'lib/api/createLocation';
import { getCustomers } from 'lib/api/getCustomers';
import {
  GLOBALTRANZ_COUNTRY_CODES,
  refreshLocationsGlobalTranz,
  searchLocationGlobalTranz,
} from 'lib/api/searchLocationGlobalTranz';
import { LoadStopTypes, TMSCustomer, TMSLocation } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import {
  GenericCompanySearchableFields,
  customerSearchHandler,
  mapCustomerToSelectOptions,
} from 'utils/loadInfoAndBuilding';

import { generateTimeOptions, getDefaultTimes } from '../utils';

interface LocationCreatorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationCreated: (location: TMSLocation) => void;
  tmsID: number;
  stopType?: LoadStopTypes;
  initialData?: Partial<LocationFormData>;
  customerBK?: string; // Optional: customerBK from selected customer in form
  /** Use the main form's customer list (same as Customer dropdown) so selected customer is always in list */
  initialCustomers?: Maybe<TMSCustomer[]>;
  initialIsLoadingCustomers?: boolean;
  /**
   * Optional callback to let the parent refetch the location list after a
   * successful GlobalTranz Address Book refresh.
   */
  onLocationsRefreshed?: () => void;
}

interface LocationFormData {
  customerBK: string; // Selected customer's externalTMSID
  companyName: string;
  // Optional keyword used only for refreshing locations from GTZ Address Book.
  // This is intentionally separate from companyName so we don't change the
  // existing create-location flow.
  refreshKeyword: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  addressLine1: string;
  addressLine2: string;
  cityStateZip: string; // Selected location from search (stores the search result)
  city: string;
  state: string;
  zipcode: string;
  latitude: number; // From search result
  longitude: number; // From search result
  country: string;
  openTime: string;
  closeTime: string;
}

export default function LocationCreatorDialog({
  isOpen,
  onClose,
  onLocationCreated,
  tmsID,
  stopType = undefined,
  initialData,
  customerBK: initialCustomerBK,
  initialCustomers,
  initialIsLoadingCustomers,
  onLocationsRefreshed,
}: LocationCreatorDialogProps) {
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [locationSearchResults, setLocationSearchResults] = useState<
    ValueType[]
  >([]);
  const latestSearchTermRef = useRef<string>('');
  const { toast } = useToast();
  const [isRefreshingLocations, setIsRefreshingLocations] = useState(false);
  const openSessionRef = useRef(0);

  // Memoize time options to avoid recalculating on every render
  const openTimeOptions = useMemo(
    () => generateTimeOptions(stopType, false, { useFullDay: false }),
    [stopType]
  );
  const closeTimeOptions = useMemo(
    () => generateTimeOptions(stopType, true, { useFullDay: false }),
    [stopType]
  );

  const defaultTimes = getDefaultTimes(stopType);

  const formMethods = useForm<LocationFormData>({
    defaultValues: {
      customerBK: initialCustomerBK || '',
      companyName: initialData?.companyName || '',
      refreshKeyword: '',
      contactName: initialData?.contactName || '',
      contactPhone: initialData?.contactPhone || '',
      contactEmail: initialData?.contactEmail || '',
      addressLine1: initialData?.addressLine1 || '',
      addressLine2: initialData?.addressLine2 || '',
      cityStateZip: '',
      city: initialData?.city || '',
      state: initialData?.state || '',
      zipcode: initialData?.zipcode || '',
      latitude: 0,
      longitude: 0,
      country: initialData?.country || 'United States',
      openTime: initialData?.openTime || defaultTimes.openTime,
      closeTime: initialData?.closeTime || defaultTimes.closeTime,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = formMethods;

  const watchedRefreshKeyword = watch('refreshKeyword');
  const watchedCustomerBK = watch('customerBK');

  const canRefreshLocationsFromAddressBook =
    !!tmsID &&
    !!watchedCustomerBK &&
    !!watchedRefreshKeyword &&
    watchedRefreshKeyword.trim().length > 0;

  const isLocationsButtonEnabled =
    canRefreshLocationsFromAddressBook && !isRefreshingLocations;

  // Use parent's customer list when modal opens (same list as main Customer dropdown)
  // Don't overwrite with null while parent is still loading — wait for definitive data so we
  // don't show an empty dropdown if the user opened the dialog before the parent fetch completed.
  useEffect(() => {
    if (isOpen) {
      if (initialIsLoadingCustomers !== undefined) {
        setIsLoadingCustomers(initialIsLoadingCustomers);
      }
      if (initialCustomers !== undefined) {
        if (!initialIsLoadingCustomers || initialCustomers !== null) {
          setCustomers(initialCustomers ?? null);
        }
      }
    }
  }, [isOpen, initialCustomers, initialIsLoadingCustomers]);

  const handleRefreshCustomers = async () => {
    if (!tmsID) return;

    setIsLoadingCustomers(true);

    const res = await getCustomers(tmsID, true);
    if (res.isOk()) {
      setCustomers(res.value.customerList);
      toast({
        description: 'Successfully refreshed customer list.',
        variant: 'success',
      });
      setIsLoadingCustomers(false);
      return;
    }
    toast({
      description: res.error.message || 'Error while refreshing customer list.',
      variant: 'destructive',
    });
    setIsLoadingCustomers(false);
  };

  const handleCustomerSearch = async (
    field: GenericCompanySearchableFields,
    value: string
  ) => {
    return customerSearchHandler({
      tmsID,
      customers,
      setCustomers,
      field,
      value,
    });
  };

  // Handle City/State/Zip search
  // Note: RHFDebounceSelect calls fetchOptions with (field, searchTerm)
  // The field parameter is the search field name (e.g., "name"), searchTerm is the actual search text
  const handleCityStateZipSearch = async (
    field: GenericCompanySearchableFields,
    searchTerm: string
  ): Promise<ValueType[]> => {
    // Ignore the field parameter - we always search by the search term
    // Make sure we have a valid search term (not just the field name)
    if (!searchTerm || searchTerm.length < 2 || searchTerm === field) {
      setLocationSearchResults([]);
      latestSearchTermRef.current = '';
      return [];
    }

    // Track the latest search term to prevent stale results
    latestSearchTermRef.current = searchTerm;

    const country = getValues('country');
    const countryCode =
      country && country in GLOBALTRANZ_COUNTRY_CODES
        ? GLOBALTRANZ_COUNTRY_CODES[
            country as keyof typeof GLOBALTRANZ_COUNTRY_CODES
          ]
        : undefined;

    const res = await searchLocationGlobalTranz(tmsID, searchTerm, countryCode);
    if (res.isOk()) {
      // Only update results if this response matches the latest search term
      // This prevents stale results from overwriting newer ones
      if (latestSearchTermRef.current !== searchTerm) {
        return [];
      }

      const options = res.value.locations.map((loc) => ({
        value: `${loc.city}, ${loc.state} ${loc.zip}`,
        label: `${loc.city}, ${loc.stateCode} ${loc.zip}`,
        name: `${loc.city}, ${loc.state} ${loc.zip}`,
        city: loc.city,
        state: loc.state,
        stateCode: loc.stateCode,
        zip: loc.zip,
        latitude: loc.latitude,
        longitude: loc.longitude,
        countryCode: loc.countryCode,
      }));
      setLocationSearchResults(options);
      return options;
    }

    // Only clear results if this was the latest search
    if (latestSearchTermRef.current === searchTerm) {
      setLocationSearchResults([]);
    }
    return [];
  };

  // Handle City/State/Zip selection
  const handleCityStateZipSelect = (selectedOption: ValueType) => {
    if (selectedOption) {
      // Update latitude/longitude first (used for validation)
      setValue('latitude', selectedOption.latitude || 0, {
        shouldValidate: false,
      });
      setValue('longitude', selectedOption.longitude || 0, {
        shouldValidate: false,
      });

      // Update the individual fields
      setValue('city', selectedOption.city || '', { shouldValidate: false });
      // Use stateCode (e.g., "NY") instead of full state name (e.g., "New York") for API
      setValue(
        'state',
        selectedOption.stateCode || selectedOption.state || '',
        { shouldValidate: false }
      );
      setValue('zipcode', selectedOption.zip || '', { shouldValidate: false });

      // Update the cityStateZip field value last and trigger validation after all fields are set
      setValue(
        'cityStateZip',
        selectedOption.value || selectedOption.name || '',
        {
          shouldValidate: true,
        }
      );

      // Set country based on countryCode (1=US, 2=CA, 3=CN, 4=MX)
      if (selectedOption.countryCode === 1) {
        setValue('country', 'United States');
      } else if (selectedOption.countryCode === 2) {
        setValue('country', 'Canada');
        // } else if (selectedOption.countryCode === 3) {
        //   setValue('country', 'China');
      } else if (selectedOption.countryCode === 4) {
        setValue('country', 'Mexico');
      }
    }
  };

  const handleRefreshLocationsFromAddressBook = async () => {
    if (!canRefreshLocationsFromAddressBook) {
      toast({
        description:
          'Please select a customer and enter a refresh keyword before refreshing.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsRefreshingLocations(true);
      const sessionId = openSessionRef.current;
      const response = await refreshLocationsGlobalTranz(
        tmsID,
        watchedCustomerBK,
        watchedRefreshKeyword.trim()
      );

      if (response.isOk()) {
        toast({
          description:
            response.value.message ||
            'Locations refreshed from GlobalTranz Address Book.',
          variant: 'success',
        });

        // Only close and notify parent if this dialog session is still active
        // (user didn't close and reopen the dialog before the API completed)
        if (sessionId === openSessionRef.current) {
          onLocationsRefreshed?.();
          handleClose();
        }
        return;
      }

      toast({
        description:
          response.error.message ||
          'Could not find matching locations. Fill the form below to add a new location for this customer.',
        variant: 'destructive',
      });
    } catch (error) {
      // Re-throw auth and extension errors so the app can handle them (e.g. redirect to re-auth)
      if (isAxiosError(error) && error.response?.status === 401) {
        throw error;
      }
      if (
        error instanceof Error &&
        error.message === 'Extension context invalidated.'
      ) {
        throw error;
      }
      toast({
        description:
          'An unexpected error occurred while refreshing locations from GlobalTranz.',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshingLocations(false);
    }
  };

  const handleClose = () => {
    reset();
    setShowAdditionalOptions(false);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      openSessionRef.current += 1;
      const defaultTimes = getDefaultTimes(stopType);
      reset(
        {
          customerBK: initialCustomerBK || '',
          companyName: initialData?.companyName || '',
          refreshKeyword: '',
          contactName: initialData?.contactName || '',
          contactPhone: initialData?.contactPhone || '',
          contactEmail: initialData?.contactEmail || '',
          addressLine1: initialData?.addressLine1 || '',
          addressLine2: initialData?.addressLine2 || '',
          cityStateZip: '',
          city: initialData?.city || '',
          state: initialData?.state || '',
          zipcode: initialData?.zipcode || '',
          latitude: 0,
          longitude: 0,
          country: initialData?.country || 'United States',
          openTime: initialData?.openTime || defaultTimes.openTime,
          closeTime: initialData?.closeTime || defaultTimes.closeTime,
        },
        {
          keepDefaultValues: false,
        }
      );

      if (
        initialData?.contactEmail ||
        initialData?.openTime ||
        initialData?.closeTime
      ) {
        setShowAdditionalOptions(true);
      }
    }
  }, [isOpen, initialData, initialCustomerBK, stopType, reset]);

  const onSubmit = async (data: LocationFormData) => {
    setIsLoading(true);

    try {
      const locationPayload: TMSLocation = {
        ID: undefined,
        externalTMSID: '', // Will be assigned by backend
        name: data.companyName,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2 || '',
        city: data.city,
        state: data.state,
        zipCode: data.zipcode,
        country: data.country,
        contact: data.contactName,
        phone: data.contactPhone,
        email: data.contactEmail || '',
        apptRequired: false,
        isShipper: !stopType || stopType === LoadStopTypes.Pickup,
        isConsignee: !stopType || stopType === LoadStopTypes.Consignee,
        openTime: data.openTime || undefined,
        closeTime: data.closeTime || undefined,
      };

      const selectedCustomerBK = data.customerBK;
      if (!selectedCustomerBK) {
        toast({
          description: 'Please select a customer.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      // Validate that lat/long are available (from search)
      if (
        data.latitude === undefined ||
        data.latitude === null ||
        data.latitude === 0 ||
        data.longitude === undefined ||
        data.longitude === null ||
        data.longitude === 0
      ) {
        toast({
          description:
            'Please select a location from the City/State/ZIP search.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      const res = await createLocation({
        location: locationPayload,
        tmsID,
        options: {
          customerBK: selectedCustomerBK,
          latitude: data.latitude,
          longitude: data.longitude,
        },
      });

      if (res.isOk()) {
        toast({
          description: 'Location created successfully!',
          variant: 'success',
        });
        onLocationCreated(res.value.location);
        handleClose();
        return;
      } else {
        toast({
          description: res.error.message || 'Error creating location.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        description: 'Error creating location.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className='max-w-md p-0 max-h-[90vh] flex flex-col border border-neutral-300 overflow-hidden'>
          <ExtendedFormProvider
            aiDefaultValues={!!initialData}
            aiIconOnly={true}
          >
            <FormProvider {...formMethods}>
              <Flex
                justify='between'
                align='center'
                className='bg-brand-main px-4 py-3 text-neutral-50 border-b rounded-t-lg [&>div>svg]:stroke-white!'
              >
                <Flex align='center' gap='sm'>
                  <MapPin className='h-5 w-5' />
                  <Typography variant='h3' className='text-neutral-50'>
                    Add Location
                  </Typography>
                </Flex>
              </Flex>

              <form
                onSubmit={(e) => {
                  e.stopPropagation();
                  handleSubmit(onSubmit)(e);
                }}
                className='flex flex-1 flex-col overflow-hidden'
              >
                <div className='flex-1 space-y-4 overflow-y-auto px-4 pb-4 [&_.ant-select-dropdown]:!overflow-visible [&_.ant-select-dropdown]:!pointer-events-auto'>
                  {/* GTZ Address Book Search */}
                  <div className='pt-3 px-3'>
                    <div className='mb-1 inline-flex items-center gap-1'>
                      <Label htmlFor='refreshKeyword' name='refreshKeyword'>
                        Search GTZ for this location
                      </Label>
                      <Tooltip>
                        <TooltipTrigger
                          type='button'
                          className='flex items-center justify-center text-neutral-400 hover:text-neutral-600 cursor-help'
                        >
                          <CircleAlert className='h-3.5 w-3.5' />
                        </TooltipTrigger>
                        <TooltipPortal container={getDrumkitContainer()}>
                          <TooltipContent
                            side='right'
                            align='center'
                            sideOffset={8}
                            className='max-w-[180px] whitespace-normal break-words text-[11px] leading-snug'
                          >
                            Type the company name or address here and click
                            refresh. We'll search your GlobalTranz account and
                            add matching locations to the dropdowns. Select a
                            customer below first to enable this search.
                          </TooltipContent>
                        </TooltipPortal>
                      </Tooltip>
                    </div>
                    <Flex align='center' gap='sm'>
                      <Input
                        id='refreshKeyword'
                        {...register('refreshKeyword')}
                        placeholder='Search by company name or address'
                      />
                      <Button
                        buttonNamePosthog={
                          ButtonNamePosthog.ImportLocationFromAddressBookGTZ
                        }
                        type='button'
                        size='sm'
                        onClick={handleRefreshLocationsFromAddressBook}
                        disabled={!isLocationsButtonEnabled}
                        className='w-[72px] shrink-0'
                      >
                        {isRefreshingLocations ? (
                          <ButtonLoader className='mr-0' />
                        ) : (
                          'Import'
                        )}
                      </Button>
                    </Flex>
                  </div>

                  <Divider text='or' className='px-3' />

                  {/* Create a location section */}
                  <div className='px-3 pb-3 space-y-2'>
                    <Typography
                      variant='subtitle2'
                      weight='semibold'
                      className='text-neutral-700'
                    >
                      Create a location
                    </Typography>

                    {/* Customer Selection */}
                    <RHFDebounceSelect
                      required
                      name='customerBK'
                      label='Customer to associate location with'
                      control={control}
                      errors={errors}
                      data={customers}
                      isLoading={isLoadingCustomers}
                      refreshHandler={handleRefreshCustomers}
                      fetchOptions={handleCustomerSearch}
                      mapOptions={mapCustomerToSelectOptions}
                      showSearchParamDropdown={false}
                      searchFieldDefault='name'
                      placeholder='Search by customer name'
                    />

                    {/* Company Name (used for creating the location) */}
                    <div className='mt-2'>
                      <Label
                        htmlFor='companyName'
                        name='companyName'
                        className='mb-1 inline-block'
                      >
                        Company Name
                      </Label>
                      <Input
                        id='companyName'
                        {...register('companyName')}
                        placeholder='Enter Company Name'
                        className={errors.companyName ? 'border-error-500' : ''}
                      />
                      {errors.companyName && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.companyName.message}
                        </Typography>
                      )}
                    </div>

                    {/* Contact Name */}
                    <div className='mt-2'>
                      <Label
                        htmlFor='contactName'
                        required
                        name='contactName'
                        className='mb-1 inline-block'
                      >
                        Contact Name
                      </Label>
                      <Input
                        id='contactName'
                        {...register('contactName', {
                          required: 'Contact name is required',
                        })}
                        placeholder='Enter Contact Name'
                        className={errors.contactName ? 'border-error-500' : ''}
                      />
                      {errors.contactName && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.contactName.message}
                        </Typography>
                      )}
                    </div>

                    {/* Contact Phone */}
                    <div className='mt-2'>
                      <Label
                        htmlFor='contactPhone'
                        required
                        name='contactPhone'
                        className='mb-1 inline-block'
                      >
                        Contact Phone
                      </Label>
                      <Controller
                        name='contactPhone'
                        control={control}
                        rules={{
                          required: 'Contact phone is required',
                          validate: (value) => {
                            if (!value) return 'Contact phone is required';
                            // Strip all non-digits and check length
                            const phoneDigits = value.replace(/[^\d]/g, '');
                            if (phoneDigits.length < 10) {
                              return 'Phone number must be at least 10 digits';
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            id='contactPhone'
                            {...field}
                            onChange={(e) => {
                              const formatted = FormatPhoneNumber(
                                e.target.value
                              );
                              field.onChange(formatted);
                            }}
                            placeholder='(555) 555-5555'
                            type='tel'
                            className={
                              errors.contactPhone ? 'border-error-500' : ''
                            }
                          />
                        )}
                      />
                      {errors.contactPhone && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.contactPhone.message}
                        </Typography>
                      )}
                    </div>

                    {/* Address Section */}
                    <div className='mt-2'>
                      <Typography variant='body' weight='semibold'>
                        Address
                      </Typography>
                    </div>

                    {/* Address Line 1 */}
                    <div className='mt-2'>
                      <Label
                        htmlFor='addressLine1'
                        required
                        name='addressLine1'
                        className='mb-1 inline-block'
                      >
                        Address Line 1
                      </Label>
                      <Input
                        id='addressLine1'
                        {...register('addressLine1', {
                          required: 'Address line 1 is required',
                        })}
                        placeholder='Enter Address Line 1'
                        className={
                          errors.addressLine1 ? 'border-error-500' : ''
                        }
                      />
                      {errors.addressLine1 && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.addressLine1.message}
                        </Typography>
                      )}
                    </div>

                    {/* Address Line 2 */}
                    <div className='mt-2'>
                      <Label
                        htmlFor='addressLine2'
                        name='addressLine2'
                        className='mb-1 inline-block'
                      >
                        Address Line 2
                      </Label>
                      <Input
                        id='addressLine2'
                        {...register('addressLine2')}
                        placeholder='Enter Address Line 2'
                      />
                    </div>

                    {/* City/State/ZIP - Searchable */}
                    <div className='mt-2'>
                      <RHFDebounceSelect
                        required
                        name='cityStateZip'
                        label='City/State/ZIP'
                        control={control}
                        errors={errors}
                        rules={{
                          required: 'City/State/ZIP is required',
                          validate: (value) => {
                            if (!value) {
                              return 'Please select a location from the search';
                            }
                            // Check if latitude/longitude are set (more reliable than city/state/zip)
                            // as they're set immediately when location is selected
                            const latitude = getValues('latitude');
                            const longitude = getValues('longitude');
                            if (
                              !latitude ||
                              !longitude ||
                              latitude === 0 ||
                              longitude === 0
                            ) {
                              return 'Please select a valid location';
                            }
                            return true;
                          },
                        }}
                        data={locationSearchResults || []}
                        isLoading={false}
                        fetchOptions={handleCityStateZipSearch}
                        mapOptions={(options) => options || []}
                        showSearchParamDropdown={false}
                        searchFieldDefault='name'
                        placeholder='Enter City, State, ZIP'
                        parentOnChange={(value) => {
                          // Find the full option object from locationSearchResults
                          // value from DebounceSelect is { label, value } due to labelInValue
                          const selectedValue =
                            typeof value === 'string'
                              ? value
                              : value?.value || value;

                          // Try to find the full option by matching value, name, or label
                          const fullOption = locationSearchResults.find(
                            (opt) =>
                              opt.value === selectedValue ||
                              opt.name === selectedValue ||
                              opt.label === selectedValue ||
                              (typeof value === 'object' &&
                                value?.label &&
                                opt.label === value.label)
                          );

                          if (fullOption) {
                            // Handle the selection to populate other fields with full option data
                            handleCityStateZipSelect(fullOption);
                          } else if (
                            value &&
                            typeof value === 'object' &&
                            (value.city || value.state || value.zip)
                          ) {
                            // Fallback: if value already has the required fields (shouldn't happen but just in case)
                            handleCityStateZipSelect(value);
                          }
                          // Note: If we can't find the option, RHFDebounceSelect will handle the field value update
                          // This might happen if locationSearchResults was cleared, but the validation will catch it
                        }}
                      />
                      {/* Hidden fields to store the actual values */}
                      <input
                        type='hidden'
                        {...register('city', { required: true })}
                      />
                      <input
                        type='hidden'
                        {...register('state', { required: true })}
                      />
                      <input
                        type='hidden'
                        {...register('zipcode', { required: true })}
                      />
                      <input type='hidden' {...register('latitude')} />
                      <input type='hidden' {...register('longitude')} />
                    </div>

                    {/* Country */}
                    <div className='mt-2'>
                      <Label
                        htmlFor='country'
                        name='country'
                        className='mb-1 inline-block'
                      >
                        Country
                      </Label>
                      <Controller
                        name='country'
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='United States'>
                                United States
                              </SelectItem>
                              <SelectItem value='Canada'>Canada</SelectItem>
                              <SelectItem value='Mexico'>Mexico</SelectItem>
                              {/* <SelectItem value='China'>China</SelectItem> */}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>

                  {/* Additional Options Toggle */}
                  <Button
                    buttonNamePosthog={null}
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() =>
                      setShowAdditionalOptions(!showAdditionalOptions)
                    }
                    className='w-full justify-between'
                  >
                    <Typography variant='body-sm'>More Options</Typography>
                    {showAdditionalOptions ? (
                      <ChevronUp className='h-4 w-4' />
                    ) : (
                      <ChevronDown className='h-4 w-4' />
                    )}
                  </Button>

                  {showAdditionalOptions && (
                    <div className='p-3'>
                      {/* Contact Email */}
                      <div>
                        <Label
                          htmlFor='contactEmail'
                          name='contactEmail'
                          className='mb-1 inline-block'
                        >
                          Contact Email
                        </Label>
                        <Input
                          id='contactEmail'
                          {...register('contactEmail')}
                          placeholder='Enter Contact Email'
                          type='email'
                        />
                      </div>

                      {/* Dock Hours */}
                      <div className='mt-2'>
                        <Typography
                          variant='body'
                          weight='semibold'
                          className='mb-2'
                        >
                          Dock Hours
                        </Typography>
                        <Grid cols='2' gap='sm'>
                          <div>
                            <Label
                              htmlFor='openTime'
                              name='openTime'
                              className='mb-1 inline-block'
                            >
                              Open Time
                            </Label>
                            <Controller
                              name='openTime'
                              control={control}
                              render={({ field }) => (
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {openTimeOptions.map((time) => (
                                      <SelectItem key={time} value={time}>
                                        {time}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          </div>

                          <div>
                            <Label
                              htmlFor='closeTime'
                              name='closeTime'
                              className='mb-1 inline-block'
                            >
                              Close Time
                            </Label>
                            <Controller
                              name='closeTime'
                              control={control}
                              render={({ field }) => (
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {closeTimeOptions.map((time) => (
                                      <SelectItem key={time} value={time}>
                                        {time}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          </div>
                        </Grid>
                      </div>
                    </div>
                  )}

                  <div className='rounded border-l-4 border-info-500 bg-info-50 p-3'>
                    <Typography variant='body-sm' className='text-neutral-700'>
                      This location will be saved to your TMS.
                    </Typography>
                  </div>
                </div>

                {/* Footer Actions */}
                <Flex
                  gap='sm'
                  className='border-t border-neutral-300 bg-neutral-100 px-4 py-3 rounded-b-lg'
                >
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.CancelLocationCreation}
                    type='button'
                    variant='outline'
                    onClick={handleClose}
                    className='flex-1 text-sm'
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.SaveLocation}
                    type='submit'
                    className='flex-1 gap-2 [&>svg]:stroke-white! text-sm'
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <Save className='h-4 w-4' />
                        Save Location
                      </>
                    )}
                  </Button>
                </Flex>
              </form>
            </FormProvider>
          </ExtendedFormProvider>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
